import {
  Checkbox,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Tooltip,
} from "@mui/material";
import {
  getClaimDescription,
  hasClaimType,
  hasClaimTypeAndValue,
  isShowDropdown,
} from "data/functions";
import {
  roleTableHeaderColumns,
  ServiceSummary,
  SortableColumns,
  SortOrder,
  TableHeaderColumn,
  User,
  UserWithClaims,
} from "data/interfaces";
import { useEffect, useState } from "react";
import RoleRow from "./RoleRow";

interface RoleTableProps {
  users: User[];
  selected: User[];
  service: ServiceSummary;
  onSelect: (selected: boolean, user: User) => void;
  onSelectAll: (allSelected: boolean) => void;
  orderBy: keyof SortableColumns;
  order: SortOrder;
  onSort: (column: string) => void;
  page: number;
  onPageChange: (page: number) => void;
}

const RoleTable = ({
  users,
  selected,
  service,
  onSelect,
  onSelectAll,
  orderBy,
  order,
  onSort,
  page,
  onPageChange,
}: RoleTableProps) => {
  const [usersWithClaims, setUsersWithClaims] = useState<UserWithClaims[]>([]);

  const [rowsPerPage, setRowsPerPage] = useState(20);

  const isSelected = (user: User) => selected?.includes(user);

  useEffect(() => {
    setUsersWithClaims(
      users.map((user: User) => {
        const userClaims = user.services.find(
          (s) => s.licenseReference === service.licenseReference,
        )?.claims;
        return { user, claims: userClaims ?? [] };
      }),
    );
  }, [service, users]);

  const handleChangeRowsPerPage = (event: any) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    onPageChange(0);
  };

  const userHasClaimType = (claimType: string, user: User) => {
    const userWithClaims = usersWithClaims.find((u) =>
      user.id ? u.user.id === user.id : u.user.email === user.email,
    );
    return userWithClaims && hasClaimType(userWithClaims.claims, claimType);
  };

  const userHasClaimTypeAndValue = (
    claimType: string,
    claimValue: string,
    user: User,
  ) => {
    const userWithClaims = usersWithClaims.find((u) =>
      user.id ? u.user.id === user.id : u.user.email === user.email,
    );
    return (
      userWithClaims &&
      hasClaimTypeAndValue(userWithClaims.claims, claimType, claimValue)
    );
  };

  const showDropdown = isShowDropdown(service);

  return (
    <Paper>
      <TableContainer>
        <Table size="small" sx={{ minWidth: 800 }}>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  checked={
                    selected.length > 0 && selected.length === users.length
                  }
                  indeterminate={
                    selected.length > 0 && selected.length < users.length
                  }
                  onChange={(e, checked) => onSelectAll(checked)}
                />
              </TableCell>
              {roleTableHeaderColumns.map((column: TableHeaderColumn) => (
                <TableCell key={column.id}>
                  {column.sortable ? (
                    <TableSortLabel
                      active={orderBy === column.id}
                      direction={orderBy === column.id ? order : "asc"}
                      onClick={() => onSort(column.id)}
                    >
                      {column.label}
                    </TableSortLabel>
                  ) : (
                    <>{column.label}</>
                  )}
                </TableCell>
              ))}
              {!showDropdown &&
                service.serviceClaims.map((claim, claimIndex) =>
                  claim.options.length >= 1 ? (
                    claim.options.map((option, optionIndex) => (
                      <TableCell
                        key={`${claimIndex}-${optionIndex}`}
                        align="center"
                      >
                        {getClaimDescription(claim, option)}
                      </TableCell>
                    ))
                  ) : (
                    <Tooltip
                      key={claimIndex}
                      placement="top"
                      title={claim.friendlyName}
                    >
                      <TableCell align="center">{claim.shortName}</TableCell>
                    </Tooltip>
                  ),
                )}
              {showDropdown && <TableCell padding="checkbox"></TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {usersWithClaims
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((userWithClaims) => (
                <RoleRow
                  key={
                    userWithClaims.user.id ??
                    `unregistered-${userWithClaims.user.email}`
                  }
                  showDropdown={showDropdown}
                  userWithClaims={userWithClaims}
                  service={service}
                  isSelected={isSelected}
                  onSelect={onSelect}
                  userHasClaimType={userHasClaimType}
                  userHasClaimTypeAndValue={userHasClaimTypeAndValue}
                />
              ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={usersWithClaims.length}
        page={page}
        onPageChange={(event: any, page: number) => {
          onPageChange(page);
        }}
        rowsPerPageOptions={[10, 20, 50]}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </Paper>
  );
};

export default RoleTable;
